/* ==========================================================================
   Wire America — mobile search overlay behaviour
   Client-approved design, 10 Sep 2026.

   WHAT THIS FILE DOES NOT DO, on purpose:
   it issues no Algolia request of its own, changes no index, query, facet or
   ranking, and does not touch algolia_headercomplete.js. That script keeps
   owning the search and keeps writing the same markup it writes today. This
   file reads what it rendered and decides which of the three states the phone
   overlay is in — the same DOM-driven approach used by wa-mobile-collection.js,
   and for the same reason: the Algolia contract is shared with the desktop
   panel and the search results page, so it stays untouched.

   The one thing it borrows is the response total. algolia_headercomplete.js
   destructures only { hits } from a response that already carries nbHits, so
   the search method is wrapped to keep that number as it goes past. No extra
   request, no second call — just a field of the same response that was being
   dropped on the floor. Without it the footer button falls back to
   "See all results" with no count.
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 749px)';
  var MODAL_SEL = '#PopupModal-mobile-search';
  var PANEL_SEL = '#header_autocomplete_mobile';
  var MIN_CHARS = 3; /* algolia_headercomplete.js searches at > 2 characters */
  var RECENT_KEY = 'wa_recent_searches';
  var RECENT_MAX = 5;

  var last = { nbHits: null, query: '' };
  var painting = false;
  var wrapped = false;

  /* ---------- helpers ---------------------------------------------------- */

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function modal() {
    return document.querySelector(MODAL_SEL);
  }

  function content() {
    var m = modal();
    return m ? m.querySelector('.product-popup-modal__content') : null;
  }

  function panel() {
    var m = modal();
    return m ? m.querySelector(PANEL_SEL) : null;
  }

  function input() {
    var m = modal();
    return m ? m.querySelector('.search-box .header-search') : null;
  }

  function form() {
    var m = modal();
    return m ? m.querySelector('.search-box form.search-bar') : null;
  }

  function query() {
    var el = input();
    return el ? el.value.trim() : '';
  }

  function icon(paths) {
    return '<svg viewBox="0 0 24 24" aria-hidden="true">' + paths + '</svg>';
  }

  var ICON_CLOCK = icon('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>');
  var ICON_CARET = icon('<path d="M9 5l7 7-7 7"/>');

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- recent searches (device only, never sent anywhere) ---------- */

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Object.prototype.toString.call(list) === '[object Array]' ? list.slice(0, RECENT_MAX) : [];
    } catch (e) {
      /* Safari private mode throws on localStorage; the idle state simply
         falls back to the category list. */
      return [];
    }
  }

  function pushRecent(term) {
    term = String(term || '').trim();
    if (term.length < MIN_CHARS) return;
    try {
      var list = readRecent().filter(function (t) {
        return t.toLowerCase() !== term.toLowerCase();
      });
      list.unshift(term);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* nothing to do — recents are a convenience, not state we rely on */
    }
    renderRecent();
  }

  function clearRecent() {
    try {
      window.localStorage.removeItem(RECENT_KEY);
    } catch (e) {}
    renderRecent();
  }

  function renderRecent() {
    var box = document.querySelector('.wa-search-idle .wa-ms-recent-block');
    if (!box) return;
    var list = readRecent();
    if (!list.length) {
      box.classList.add('wa-ms-hide');
      box.innerHTML = '';
      return;
    }
    box.classList.remove('wa-ms-hide');
    var html =
      '<div class="wa-ms-label"><span>Recent</span><button type="button" class="wa-ms-clear-recent">Clear</button></div>' +
      '<ul class="wa-ms-list wa-ms-recent">';
    for (var i = 0; i < list.length; i++) {
      html +=
        '<li><a href="#" data-wa-term="' + escapeHtml(list[i]) + '">' +
        ICON_CLOCK + '<span>' + escapeHtml(list[i]) + '</span></a></li>';
    }
    box.innerHTML = html + '</ul>';
  }

  /* ---------- the search results URL -------------------------------------
     Built from the modal's own form so the button lands exactly where pressing
     Enter lands, including the hidden type / prefix / unavailable_products
     fields. Nothing here assumes a parameter name. */

  function resultsUrl(term) {
    var f = form();
    if (!f) return '/search?q=' + encodeURIComponent(term);
    var params = new URLSearchParams();
    var fields = f.querySelectorAll('input[name]');
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      params.set(el.name, el.classList.contains('header-search') ? term : el.value);
    }
    return (f.getAttribute('action') || '/search') + '?' + params.toString();
  }

  /* ---------- state ------------------------------------------------------ */

  function currentMode(q) {
    if (q.length < MIN_CHARS) return 'idle';
    var p = panel();
    /* algolia_headercomplete.js writes .autocomplete_noresults into the product
       list when the products index returns nothing. */
    var empty = p && p.querySelector('.search-product .autocomplete_noresults');
    return empty ? 'noresults' : 'results';
  }

  function decorate() {
    if (painting || !isMobile()) return;
    var c = content();
    var p = panel();
    if (!c || !p) return;

    painting = true;
    try {
      var q = query();
      var mode = currentMode(q);

      c.classList.toggle('wa-ms-idle', mode === 'idle');
      c.classList.toggle('wa-ms-results', mode === 'results');
      c.classList.toggle('wa-ms-noresults', mode === 'noresults');

      /* Product count: the panel's own label reads "N Products" but N is capped
         at the 4 hits it asked for, so the real total comes from the response. */
      var rows = p.querySelectorAll('.search-product .autocomplete_result').length;
      var total = last.query === q && typeof last.nbHits === 'number' ? last.nbHits : null;

      var label = p.querySelector('.search-product .autocomplete_label');
      if (label && mode === 'results') {
        var wanted =
          '<span>Products</span><span class="wa-ms-count">' +
          (total === null ? rows : total) + '</span>';
        if (label.innerHTML !== wanted) label.innerHTML = wanted;
      }

      /* Categories: hidden when the collections index returned nothing, so an
         empty "Categories" heading never sits above the products. */
      var cat = p.querySelector('.search-category');
      var catShown = false;
      if (cat) {
        var catRows = cat.querySelectorAll('.autocomplete_results li').length;
        var catEmpty = !!cat.querySelector('.autocomplete_noresults');
        catShown = catRows > 0 && !catEmpty;
        cat.classList.toggle('wa-ms-hide', !catShown);
      }

      /* No-results falls back to the standing category list, but only when the
         collections index had nothing of its own to offer. */
      var popular = c.querySelector('.wa-ms-popular-block');
      if (popular) popular.classList.toggle('wa-ms-hide', mode !== 'noresults' || catShown);

      /* Footer button */
      var btn = c.querySelector('.wa-ms-seeall');
      if (btn) {
        var text = total === null ? 'See all results' : 'See all ' + total + ' result' + (total === 1 ? '' : 's');
        if (btn.textContent !== text) btn.textContent = text;
        btn.setAttribute('href', resultsUrl(q));
      }

      /* No-results copy carries the term the shopper actually typed. */
      var term = c.querySelector('.wa-ms-empty-term');
      if (term && mode === 'noresults' && term.textContent !== q) {
        term.textContent = q;
      }
    } finally {
      painting = false;
    }
  }

  /* ---------- keep nbHits as it goes past --------------------------------- */

  function wrapClient() {
    if (wrapped) return;
    try {
      /* headerClient is a top-level const in algolia_headercomplete.js, so it
         is a global lexical binding once that script has run. */
      if (typeof headerClient === 'undefined' || !headerClient || typeof headerClient.search !== 'function') return;
      var original = headerClient.search.bind(headerClient);
      headerClient.search = function (term, options) {
        var promise = original(term, options);
        try {
          promise.then(function (res) {
            if (res && typeof res.nbHits === 'number') {
              last.nbHits = res.nbHits;
              last.query = String(term || '').trim();
            }
          });
        } catch (e) {}
        return promise;
      };
      wrapped = true;
    } catch (e) {
      /* leaves the footer button reading "See all results" — no count, no harm */
    }
  }

  /* ---------- wiring ------------------------------------------------------ */

  function onInput() {
    wrapClient();
    decorate();
  }

  function closeOverlay() {
    var el = input();
    if (el) el.value = '';
    decorate();
    var close = document.querySelector('#ModalClose-mobile-search');
    if (close) close.click();
  }

  function onClick(e) {
    var m = modal();
    if (!m || !m.contains(e.target)) return;

    var cancel = e.target.closest('.wa-search-cancel');
    if (cancel) {
      e.preventDefault();
      closeOverlay();
      return;
    }

    var clear = e.target.closest('.wa-ms-clear-recent');
    if (clear) {
      e.preventDefault();
      clearRecent();
      return;
    }

    /* A recent term re-runs the real search by replaying an input event, so
       algolia_headercomplete.js does the searching exactly as it would if the
       shopper had typed it. */
    var recent = e.target.closest('[data-wa-term]');
    if (recent) {
      e.preventDefault();
      var el = input();
      if (el) {
        el.value = recent.getAttribute('data-wa-term');
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.focus();
      }
      return;
    }

    if (e.target.closest('.wa-ms-seeall') || e.target.closest('.autocomplete_title')) {
      pushRecent(query());
    }
  }

  function onSubmit(e) {
    var f = form();
    if (f && e.target === f) pushRecent(query());
  }

  function observe() {
    var p = panel();
    if (!p || p.dataset.waMsObserved) return;
    p.dataset.waMsObserved = '1';
    /* No render event exists to hook, so the panel's own mutations are the
       signal that a search finished painting. */
    new MutationObserver(function () {
      if (!painting) decorate();
    }).observe(p, { childList: true, subtree: true, characterData: true });
  }

  function init() {
    if (!modal()) return;
    wrapClient();
    observe();
    renderRecent();
    decorate();

    var el = input();
    if (el) el.addEventListener('focus', onInput);

    /* Capture phase on the document, so this runs BEFORE the jQuery input
       handler in algolia_headercomplete.js that fires the search. Bound to the
       element it would run after that handler instead -- listeners on the
       target fire in registration order and jQuery got there first -- and the
       very first search of a session would go past unwrapped, which is exactly
       when a shopper types a 3-character term and sees no count. */
    document.addEventListener('input', function (e) {
      if (e.target && e.target.classList && e.target.classList.contains('header-search')) onInput();
    }, true);

    /* algolia_headercomplete.js may be a later defer script than this one, in
       which case headerClient does not exist yet at first run. */
    if (!wrapped) {
      var tries = 0;
      var timer = setInterval(function () {
        wrapClient();
        if (wrapped || ++tries > 60) clearInterval(timer);
      }, 50);
    }
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);

    /* Opening the modal should land on the idle state, not on whatever the
       last search left behind. */
    var opener = document.querySelector('#ModalOpen-mobile-search');
    if (opener) opener.addEventListener('click', function () { setTimeout(decorate, 0); });

    window.matchMedia(MOBILE_MQ).addEventListener('change', decorate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
